import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';

export type MorphMode = 'erode' | 'dilate';

/** One separable morphology operation on an `r32float` alpha map. */
export function morphology(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  radius: number,
  mode: MorphMode,
): void {
  const r = Math.max(0, Math.round(radius));
  if (r === 0 && src === dst) return;
  const temp = ctx.image(src.width, src.height, 'r32float', 'morph-temp');
  axisPass(ctx, src, temp, r, 0, mode);
  axisPass(ctx, temp, dst, r, 1, mode);
  temp.release();
}

/**
 * Choke/spread: positive grows the matte, negative shrinks it.
 *
 * Shrinking by a pixel is the standard fix for the pale ring a light mockup
 * background leaves around dark artwork; growing recovers strokes a network
 * trimmed too aggressively.
 */
export function chokeSpread(ctx: GpuContext, src: GpuImage, dst: GpuImage, amount: number): void {
  const radius = Math.round(Math.abs(amount));
  if (radius === 0) {
    if (src !== dst) copyAlpha(ctx, src, dst);
    return;
  }
  morphology(ctx, src, dst, radius, amount > 0 ? 'dilate' : 'erode');
}

/** Erode then dilate: deletes speckle without moving surviving edges. */
export function open(ctx: GpuContext, src: GpuImage, dst: GpuImage, radius: number): void {
  const temp = ctx.image(src.width, src.height, 'r32float', 'morph-open');
  morphology(ctx, src, temp, radius, 'erode');
  morphology(ctx, temp, dst, radius, 'dilate');
  temp.release();
}

/** Dilate then erode: fills pinholes inside otherwise solid artwork. */
export function close(ctx: GpuContext, src: GpuImage, dst: GpuImage, radius: number): void {
  const temp = ctx.image(src.width, src.height, 'r32float', 'morph-close');
  morphology(ctx, src, temp, radius, 'dilate');
  morphology(ctx, temp, dst, radius, 'erode');
  temp.release();
}

function copyAlpha(ctx: GpuContext, src: GpuImage, dst: GpuImage): void {
  // A radius-0 dilate is an identity copy and avoids a second shader.
  axisPass(ctx, src, dst, 0, 0, 'dilate');
}

function axisPass(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  radius: number,
  axis: 0 | 1,
  mode: MorphMode,
): void {
  ctx.dispatch(
    {
      module: 'morphology.wgsl',
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'storageTexture', format: 'r32float' },
        { kind: 'uniform' },
      ],
    },
    [
      src,
      dst,
      ctx.uniform(
        new Struct(32).i(0, src.width, src.height).i(16, radius, axis, mode === 'dilate' ? 1 : 0, 0)
          .bytes,
      ),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    `morph:${mode} r=${radius} axis=${axis}`,
  );
}
